import { useCallback, useMemo, useRef } from "react";
import { useLocation, useNavigate, useRouter, useBlocker } from "@tanstack/react-router";
import { readWorkspaceNavigation, workspaceNavigationHref, type WorkspaceNavigation } from "../lib/workspaceNavigation";

/** Route state is authoritative; menu actions are batched into one history entry. */
export function useBuilderRouteNavigation(dirty: boolean) {
  const location = useLocation();
  const navigate = useNavigate();
  const router = useRouter();
  useBlocker({
    shouldBlockFn: ({ current, next }) => dirty && current.pathname !== next.pathname && !window.confirm("Leave this screen with unsaved changes?"),
    enableBeforeUnload: dirty,
  });
  const value = useMemo(() => readWorkspaceNavigation(location.pathname, location.searchStr), [location.pathname, location.searchStr]);
  const pending = useRef<{ value: WorkspaceNavigation; replace: boolean } | null>(null);
  const update = useCallback((change: Partial<WorkspaceNavigation>, replace = false) => {
    const queued = pending.current;
    const current = queued?.value ?? readWorkspaceNavigation(router.state.location.pathname, router.state.location.searchStr);
    pending.current = { value: { ...current, ...change }, replace: queued ? queued.replace && replace : replace };
    if (queued) return;
    // A menu action can change the area, subsection and item together: one history entry.
    queueMicrotask(() => {
      const next = pending.current;
      pending.current = null;
      if (!next) return;
      const target = workspaceNavigationHref(next.value, router.state.location.searchStr, router.state.location.hash);
      if (new URL(target, window.location.origin).href === new URL(router.state.location.href, window.location.origin).href) return;
      void navigate({ href: target, replace: next.replace });
    });
  }, [navigate, router]);
  const removeSearchParameter = useCallback((name: string) => {
    void navigate({ to: ".", replace: true, search: previous => {
      const search = { ...previous } as Record<string, unknown>;
      delete search[name];
      return search;
    } });
  }, [navigate]);
  return useMemo(() => ({ value, update, removeSearchParameter }), [value, update, removeSearchParameter]);
}
